(function () {
  'use strict';

  var STORAGE_KEY = 'nailinfinity_wishlist';
  var config = window.wishlistConfig || {};

  function parseItems(raw) {
    if (!raw) return new Map();

    var data = raw;

    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch (error) {
        return new Map();
      }
    }

    var list = Array.isArray(data) ? data : (data.items || []);
    var map = new Map();

    list.forEach(function (item) {
      if (!item || !item.id) return;
      map.set(String(item.id), {
        id: String(item.id),
        handle: item.handle || '',
        addedAt: item.addedAt || new Date().toISOString()
      });
    });

    return map;
  }

  function mapToArray(map) {
    return Array.from(map.values()).sort(function (a, b) {
      return new Date(b.addedAt) - new Date(a.addedAt);
    });
  }

  function mergeMaps(primary, secondary) {
    var merged = new Map(primary);

    secondary.forEach(function (item, id) {
      if (!merged.has(id)) {
        merged.set(id, item);
      }
    });

    return merged;
  }

  var strings = config.strings || {};

  var Wishlist = {
    items: new Map(),
    isLoggedIn: !!config.isLoggedIn,
    isReady: false,
    proxyUrl: config.proxyUrl || '/apps/wishlist',
    wishlistPageUrl: config.wishlistPageUrl || '/pages/velmju-lapa',
    isSaving: false,

    init: function () {
      var self = this;

      this.load().then(function () {
        self.isReady = true;
        self.bindEvents();
        self.updateUI();
        document.dispatchEvent(new CustomEvent('wishlist:ready', {
          detail: { count: self.items.size }
        }));
      });
    },

    load: function () {
      var self = this;
      var localItems = parseItems(localStorage.getItem(STORAGE_KEY));
      var serverItems = parseItems(config.customerWishlist);

      if (this.isLoggedIn) {
        this.items = mergeMaps(serverItems, localItems);

        if (localItems.size > 0 && this.shouldSync(localItems, serverItems)) {
          return this.saveToServer().then(function () {
            self.saveToLocal();
          }).catch(function () {
            self.saveToLocal();
          });
        }

        this.saveToLocal();
        return Promise.resolve();
      }

      this.items = localItems;
      return Promise.resolve();
    },

    shouldSync: function (localItems, serverItems) {
      var needsSync = false;

      localItems.forEach(function (item, id) {
        if (!serverItems.has(id)) {
          needsSync = true;
        }
      });

      return needsSync || localItems.size !== serverItems.size;
    },

    bindEvents: function () {
      var self = this;

      document.addEventListener('click', function (event) {
        var toggle = event.target.closest('[data-wishlist-toggle]');
        if (!toggle) return;

        event.preventDefault();
        event.stopPropagation();

        self.toggle(
          toggle.getAttribute('data-product-id'),
          toggle.getAttribute('data-product-handle')
        );
      });

      document.addEventListener('click', function (event) {
        var shareButton = event.target.closest('[data-wishlist-share]');
        if (!shareButton) return;

        event.preventDefault();
        self.share();
      });

      document.addEventListener('shopify:section:load', function () {
        self.updateUI();
      });
    },

    toggle: function (productId, handle) {
      var id = String(productId);

      if (this.items.has(id)) {
        this.items.delete(id);
      } else {
        this.items.set(id, {
          id: id,
          handle: handle,
          addedAt: new Date().toISOString()
        });
      }

      this.saveToLocal();

      if (this.isLoggedIn) {
        this.saveToServer();
      }

      this.updateUI();
      this.dispatchUpdate();
    },

    remove: function (productId) {
      this.items.delete(String(productId));
      this.saveToLocal();

      if (this.isLoggedIn) {
        this.saveToServer();
      }

      this.updateUI();
      this.dispatchUpdate();
    },

    saveToLocal: function () {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        items: mapToArray(this.items)
      }));
    },

    saveToServer: function () {
      var self = this;

      if (!this.isLoggedIn || this.isSaving) {
        return Promise.resolve();
      }

      this.isSaving = true;

      return fetch(this.proxyUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: mapToArray(this.items)
        })
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Wishlist sync failed');
        }
        return response.json();
      }).catch(function (error) {
        console.warn('Wishlist sync error:', error);
      }).finally(function () {
        self.isSaving = false;
      });
    },

    getCount: function () {
      return this.items.size;
    },

    getProductIds: function () {
      return mapToArray(this.items).map(function (item) {
        return item.id;
      });
    },

    getShareUrl: function () {
      var handles = mapToArray(this.items).map(function (item) {
        return item.handle;
      }).filter(Boolean);

      if (!handles.length) {
        return window.location.origin + this.wishlistPageUrl;
      }

      return window.location.origin + this.wishlistPageUrl + '?wishlist=' + encodeURIComponent(handles.join(','));
    },

    share: function () {
      var url = this.getShareUrl();
      var feedback = document.querySelector('[data-wishlist-share-feedback]');

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          if (feedback) {
            feedback.textContent = strings.copied || 'Link copied!';
            feedback.hidden = false;
          }
        }).catch(function () {
          window.prompt(strings.copyPrompt || 'Copy this link:', url);
        });
        return;
      }

      window.prompt(strings.copyPrompt || 'Copy this link:', url);
    },

    updateUI: function () {
      var self = this;
      var count = this.getCount();
      var addLabel = strings.add || 'Add to wishlist';
      var removeLabel = strings.remove || 'Remove from wishlist';

      document.querySelectorAll('[data-wishlist-count]').forEach(function (element) {
        element.textContent = count;
        element.setAttribute('data-count', String(count));
      });

      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
        var productId = String(button.getAttribute('data-product-id'));
        var isActive = self.items.has(productId);

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        button.setAttribute('aria-label', isActive ? removeLabel : addLabel);

        var label = button.querySelector('.wishlist-button__label');
        if (label) {
          label.textContent = isActive ? removeLabel : addLabel;
        }
      });
    },

    dispatchUpdate: function () {
      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: {
          count: this.getCount(),
          items: mapToArray(this.items)
        }
      }));
    }
  };

  window.NailinfinityWishlist = Wishlist;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      Wishlist.init();
    });
  } else {
    Wishlist.init();
  }
})();
